<script setup lang="ts">
import { Head, Link, router } from '@inertiajs/vue3'
import PageHeading from "@/Components/Headings/PageHeading.vue"
import { PageHeading as PageHeadingTS } from '@/types/PageHeading'
import Button from '@/Components/Utils/Button.vue'
import ModalConfirmationDelete from '@/Components/Utils/ModalConfirmationDelete.vue'
import { useFormatTime } from '@/Composables/useFormatTime'
import { notify } from '@kyvg/vue3-notification'
import { ref, watch } from 'vue'
import axios from 'axios'
import { debounce } from 'lodash-es'
import Dialog from 'primevue/dialog'
import InputText from 'primevue/inputtext'
import DatePicker from 'primevue/datepicker'
import Textarea from 'primevue/textarea'
import Icon from '@/Components/Utils/Icon.vue'
import LoadingIcon from '@/Components/Utils/LoadingIcon.vue'

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faSearch, faCheck, faTruck, faTrash, faTimes } from '@fal'
import { library } from '@fortawesome/fontawesome-svg-core'
library.add(faSearch, faCheck, faTruck, faTrash, faTimes)

const props = defineProps<{
    pageHead?: PageHeadingTS
    title?: string
    delivery_note: {
        id: number
        slug: string
        reference: string
        delivery_date: string | null
        note: string | null
        customer_id: number | null
        recipient_name: string | null
        recipient_address: string | null
        recipient_phone: string | null
        created_by: { id: number; name: string } | null
        orders: Array<{
            id: number
            slug: string
            reference: string
            state: string
            state_label: string
            state_icon: { icon: string; class: string; tooltip: string }
            type: string
            type_icon: { icon: string; class: string; tooltip: string }
            quantity: number
            dimension_width: number | null
            dimension_height: number | null
            customer: { id: number; name: string; slug: string } | null
            product_name: string | null
            created_at: string
        }>
        created_at: string
    }
}>()

// -- Edit delivery note fields
const isEditModalOpen = ref(false)
const editDeliveryDate = ref<Date | null>(null)
const editNote = ref('')
const editRecipientName = ref('')
const editRecipientAddress = ref('')
const editRecipientPhone = ref('')
const editCustomerId = ref<number | null>(null)
const isLoadingEdit = ref(false)

// Customer search for edit modal
const editCustomerQuery = ref('')
const isLoadingEditCustomer = ref(false)
const listEditCustomer = ref<any[]>([])

const fetchEditCustomers = async (q = '') => {
    isLoadingEditCustomer.value = true
    try {
        const { data } = await axios.get(route('json.customers'), { params: { q } })
        listEditCustomer.value = data.data ?? data
    } catch {
        notify({ title: 'Oops!', text: 'Gagal mencari pelanggan', type: 'error' })
    } finally {
        isLoadingEditCustomer.value = false
    }
}
const debFetchEditCustomer = debounce((q: string) => fetchEditCustomers(q), 350)
watch(editCustomerQuery, (val) => {
    if (val) debFetchEditCustomer(val)
    else listEditCustomer.value = []
})

const selectEditCustomer = (customer: any) => {
    editCustomerId.value = customer.id
    editRecipientName.value = customer.name
    editRecipientAddress.value = customer.address ?? ''
    editRecipientPhone.value = customer.phone ?? ''
    editCustomerQuery.value = ''
    listEditCustomer.value = []
}

const clearEditCustomer = () => {
    editCustomerId.value = null
    editRecipientName.value = ''
    editRecipientAddress.value = ''
    editRecipientPhone.value = ''
}

const openEditModal = () => {
    editDeliveryDate.value = props.delivery_note.delivery_date ? new Date(props.delivery_note.delivery_date) : null
    editNote.value = props.delivery_note.note ?? ''
    editCustomerId.value = props.delivery_note.customer_id
    editRecipientName.value = props.delivery_note.recipient_name ?? ''
    editRecipientAddress.value = props.delivery_note.recipient_address ?? ''
    editRecipientPhone.value = props.delivery_note.recipient_phone ?? ''
    editCustomerQuery.value = ''
    listEditCustomer.value = []
    isEditModalOpen.value = true
}

const submitEdit = () => {
    router.patch(
        route('casheer.delivery_notes.update', props.delivery_note.id),
        {
            delivery_date:      editDeliveryDate.value ? editDeliveryDate.value.toISOString() : null,
            note:               editNote.value || null,
            customer_id:        editCustomerId.value,
            recipient_name:     editRecipientName.value || null,
            recipient_address:  editRecipientAddress.value || null,
            recipient_phone:    editRecipientPhone.value || null,
        },
        {
            onStart: () => (isLoadingEdit.value = true),
            onFinish: () => (isLoadingEdit.value = false),
            onSuccess: () => {
                isEditModalOpen.value = false
                notify({ title: 'Berhasil', text: 'Surat Jalan diperbarui.', type: 'success' })
            },
            onError: () => notify({ title: 'Oops!', text: 'Gagal memperbarui Surat Jalan.', type: 'error' }),
        }
    )
}

// -- Add order modal
const isAddOrderOpen = ref(false)
const searchOrderQuery = ref('')
const isLoadingOrder = ref(false)
const listOrder = ref<any[]>([])
const selectedOrderIds = ref<number[]>([])

const fetchOrder = async (q = '') => {
    isLoadingOrder.value = true
    try {
        const { data } = await axios.get(route('json.orders.finished'), { params: { q } })
        listOrder.value = data.data ?? data
    } catch {
        notify({ title: 'Oops!', text: 'Gagal mencari data Order', type: 'error' })
    } finally {
        isLoadingOrder.value = false
    }
}

const debFetchOrder = debounce((q: string) => fetchOrder(q), 350)
watch(searchOrderQuery, (val) => debFetchOrder(val))

const toggleOrder = (id: number) => {
    const idx = selectedOrderIds.value.indexOf(id)
    if (idx === -1) {
        selectedOrderIds.value.push(id)
    } else {
        selectedOrderIds.value.splice(idx, 1)
    }
}

const openAddOrder = () => {
    selectedOrderIds.value = []
    searchOrderQuery.value = ''
    fetchOrder()
    isAddOrderOpen.value = true
}

const isLoadingAddOrder = ref(false)
const submitAddOrder = () => {
    router.patch(
        route('casheer.delivery_notes.add_order', props.delivery_note.id),
        { orders: selectedOrderIds.value },
        {
            onStart: () => (isLoadingAddOrder.value = true),
            onFinish: () => (isLoadingAddOrder.value = false),
            onSuccess: () => {
                isAddOrderOpen.value = false
                notify({ title: 'Berhasil', text: 'Order ditambahkan.', type: 'success' })
            },
            onError: () => notify({ title: 'Oops!', text: 'Gagal menambahkan order.', type: 'error' }),
        }
    )
}

// -- Remove order
const removeOrder = (orderId: number) => {
    router.patch(
        route('casheer.delivery_notes.remove_order', [props.delivery_note.id, orderId]),
        {},
        {
            onSuccess: () => notify({ title: 'Berhasil', text: 'Order dihapus dari Surat Jalan.', type: 'success' }),
            onError: () => notify({ title: 'Oops!', text: 'Gagal menghapus order.', type: 'error' }),
        }
    )
}
</script>

<template>
    <Head :title="title" />
    <PageHeading :data="pageHead">
        <template #other>
            <a :href="route('casheer.delivery_notes.pdf', delivery_note.slug)" target="_blank">
                <Button label="Cetak PDF" icon="fal fa-print" style="secondary" />
            </a>
        </template>

        <template #button-delete-delivery-note="{ action }">
            <ModalConfirmationDelete
                :routeDelete="action.route"
                :title="'Yakin ingin menghapus ' + delivery_note.reference + '?'"
                :description="delivery_note.orders.length ? `Surat Jalan akan dihapus permanen. ${delivery_note.orders.length} order akan dilepas dan bisa dimasukkan ke Surat Jalan lain.` : 'Surat Jalan akan dihapus permanen.'"
                isFullLoading
                @success="() => notify({ title: 'Berhasil', text: 'Surat Jalan dihapus!', type: 'success' })"
            >
                <template #default="{ changeModel }">
                    <Button
                        @click="changeModel()"
                        :style="action.style"
                        icon="fal fa-trash-alt"
                        label="Hapus"
                    />
                </template>
            </ModalConfirmationDelete>
        </template>
    </PageHeading>

    <!-- Info card -->
    <div class="mx-auto max-w-5xl px-4 py-6 space-y-6">
        <div class="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
            <div class="flex items-center justify-between mb-4">
                <h2 class="font-semibold text-gray-700 text-sm uppercase tracking-wide">Informasi Surat Jalan</h2>
                <Button label="Edit" icon="fal fa-pencil" type="secondary" size="xs" @click="openEditModal" />
            </div>
            <dl class="grid grid-cols-2 gap-x-8 gap-y-3 text-sm">
                <div>
                    <dt class="text-gray-500">Nomor SJ</dt>
                    <dd class="font-semibold">{{ delivery_note.reference }}</dd>
                </div>
                <div>
                    <dt class="text-gray-500">Dibuat Oleh</dt>
                    <dd>{{ delivery_note.created_by?.name ?? '-' }}</dd>
                </div>
                <div>
                    <dt class="text-gray-500">Tanggal Pengiriman</dt>
                    <dd>{{ delivery_note.delivery_date ? useFormatTime(delivery_note.delivery_date) : '-' }}</dd>
                </div>
                <div>
                    <dt class="text-gray-500">Tanggal Dibuat</dt>
                    <dd>{{ useFormatTime(delivery_note.created_at) }}</dd>
                </div>
                <div>
                    <dt class="text-gray-500">Penerima</dt>
                    <dd>{{ delivery_note.recipient_name || '-' }}</dd>
                </div>
                <div>
                    <dt class="text-gray-500">No. HP Penerima</dt>
                    <dd>{{ delivery_note.recipient_phone || '-' }}</dd>
                </div>
                <div class="col-span-2">
                    <dt class="text-gray-500">Alamat Penerima</dt>
                    <dd class="whitespace-pre-line">{{ delivery_note.recipient_address || '-' }}</dd>
                </div>
                <div class="col-span-2">
                    <dt class="text-gray-500">Catatan</dt>
                    <dd class="whitespace-pre-line">{{ delivery_note.note || '-' }}</dd>
                </div>
            </dl>
        </div>

        <!-- Orders list -->
        <div class="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
            <div class="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                <h2 class="font-semibold text-gray-700 text-sm uppercase tracking-wide">
                    Daftar Order
                    <span class="ml-1 text-gray-400 font-normal">({{ delivery_note.orders.length }})</span>
                </h2>
                <Button label="Tambah Order" type="dashed" icon="fal fa-plus" size="xs" @click="openAddOrder" />
            </div>

            <div v-if="!delivery_note.orders.length" class="flex items-center justify-center py-12 text-gray-400 text-sm italic">
                Belum ada order
            </div>
            <ul v-else class="divide-y divide-gray-100">
                <li
                    v-for="order in delivery_note.orders"
                    :key="order.id"
                    class="flex items-center gap-3 px-5 py-3 hover:bg-gray-50"
                >
                    <div class="flex-1 min-w-0">
                        <div class="flex items-center gap-2">
                            <Link :href="route('casheer.orders.show', order.slug)" class="primaryLink font-semibold text-sm">
                                {{ order.reference }}
                            </Link>
                            <Icon v-if="order.type_icon" :data="order.type_icon" class="text-xs" />
                            <Icon v-if="order.state_icon" :data="order.state_icon" class="text-xs" />
                        </div>
                        <div class="text-xs text-gray-500 mt-0.5 flex gap-2">
                            <span>{{ order.customer?.name ?? 'Tanpa pelanggan' }}</span>
                            <span>·</span>
                            <span>{{ order.product_name ?? '-' }}</span>
                            <template v-if="order.type === 'flexo'">
                                <span>·</span>
                                <span>{{ order.dimension_width || 0 }}x{{ order.dimension_height || 0 }} cm</span>
                            </template>
                            <span>·</span>
                            <span>Qty: {{ order.quantity }}</span>
                            <span>·</span>
                            <span>{{ useFormatTime(order.created_at) }}</span>
                        </div>
                    </div>
                    <button
                        class="text-red-400 hover:text-red-600 transition-colors p-1"
                        title="Hapus dari Surat Jalan"
                        @click="removeOrder(order.id)"
                    >
                        <FontAwesomeIcon icon="fal fa-times" class="text-sm" fixed-width />
                    </button>
                </li>
            </ul>
        </div>
    </div>

    <!-- Edit modal -->
    <Dialog v-model:visible="isEditModalOpen" modal header="Edit Surat Jalan" :style="{ width: '32rem' }">
        <div class="space-y-4">
            <div>
                <label class="block text-sm font-medium mb-1">Tanggal Pengiriman</label>
                <DatePicker v-model="editDeliveryDate" showTime showIcon class="w-full" dateFormat="dd/mm/yy" />
            </div>

            <!-- Recipient -->
            <div>
                <div class="flex items-center justify-between mb-2">
                    <label class="block text-sm font-medium">Penerima</label>
                    <button
                        v-if="editCustomerId || editRecipientName"
                        type="button"
                        class="text-xs text-red-400 hover:text-red-600"
                        @click="clearEditCustomer"
                    >
                        <FontAwesomeIcon icon="fal fa-times" class="mr-1" />Hapus penerima
                    </button>
                </div>

                <!-- Customer search autocomplete -->
                <div class="mb-2 relative">
                    <span class="p-input-icon-left w-full relative">
                        <FontAwesomeIcon icon="fal fa-search" class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                        <InputText
                            v-model="editCustomerQuery"
                            placeholder="Cari pelanggan untuk auto-isi…"
                            class="w-full !pl-9"
                        />
                    </span>
                    <div v-if="isLoadingEditCustomer" class="absolute z-10 w-full bg-white border border-gray-200 rounded-md shadow mt-1 flex items-center justify-center py-2">
                        <LoadingIcon />
                    </div>
                    <ul v-else-if="listEditCustomer.length" class="absolute z-10 w-full bg-white border border-gray-200 rounded-md shadow mt-1 max-h-40 overflow-y-auto">
                        <li
                            v-for="c in listEditCustomer"
                            :key="c.id"
                            class="px-3 py-2 cursor-pointer hover:bg-sky-50 text-sm"
                            @click="selectEditCustomer(c)"
                        >
                            <span class="font-semibold">{{ c.name }}</span>
                            <span v-if="c.phone" class="ml-2 text-gray-400 text-xs">{{ c.phone }}</span>
                        </li>
                    </ul>
                </div>

                <div class="space-y-2">
                    <InputText v-model="editRecipientName" placeholder="Nama penerima" class="w-full" />
                    <InputText v-model="editRecipientPhone" placeholder="No. HP penerima" class="w-full" />
                    <Textarea v-model="editRecipientAddress" rows="4" class="w-full" placeholder="Alamat penerima" />
                </div>
            </div>

            <div>
                <label class="block text-sm font-medium mb-1">Catatan</label>
                <Textarea v-model="editNote" rows="3" class="w-full" placeholder="Catatan (opsional)" />
            </div>
        </div>
        <div class="flex justify-end gap-2 mt-4">
            <Button label="Batal" type="tertiary" @click="isEditModalOpen = false" />
            <Button label="Simpan" :loading="isLoadingEdit" full @click="submitEdit" />
        </div>
    </Dialog>

    <!-- Add order modal -->
    <Dialog v-model:visible="isAddOrderOpen" modal header="Tambah Order ke Surat Jalan" :style="{ width: '32rem' }">
        <div class="mb-3">
            <label class="block text-sm font-medium mb-1">Cari Order</label>
            <span class="p-input-icon-left w-full relative">
                <FontAwesomeIcon icon="fal fa-search" class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                <InputText
                    v-model="searchOrderQuery"
                    placeholder="No. order, nama pelanggan, hari, bulan, tahun…"
                    class="w-full !pl-9"
                />
            </span>
        </div>

        <div class="border border-gray-200 rounded-md overflow-y-auto max-h-72 min-h-20">
            <div v-if="isLoadingOrder" class="flex items-center justify-center h-20 text-gray-400">
                <LoadingIcon />
            </div>
            <div v-else-if="!listOrder.length" class="flex items-center justify-center h-20 text-gray-400 text-sm italic">
                Tidak ada order yang tersedia
            </div>
            <ul v-else class="divide-y divide-gray-100">
                <li
                    v-for="order in listOrder"
                    :key="order.id"
                    class="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50 select-none"
                    :class="selectedOrderIds.includes(order.id) ? 'bg-sky-50' : ''"
                    @click="toggleOrder(order.id)"
                >
                    <div
                        class="w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-colors"
                        :class="selectedOrderIds.includes(order.id) ? 'bg-sky-500 border-sky-500 text-white' : 'border-gray-300'"
                    >
                        <FontAwesomeIcon v-if="selectedOrderIds.includes(order.id)" icon="fal fa-check" class="text-xs" />
                    </div>
                    <div class="flex-1 min-w-0">
                        <div class="flex items-center gap-2">
                            <span class="font-semibold text-sm">{{ order.reference }}</span>
                            <Icon v-if="order.type_icon" :data="order.type_icon" class="text-xs" />
                            <Icon v-if="order.state_icon" :data="order.state_icon" class="text-xs" />
                        </div>
                        <div class="text-xs text-gray-500 truncate">
                            <span>{{ order.customer?.name ?? 'Tanpa pelanggan' }}</span>
                            <span class="mx-1">·</span>
                            <span>{{ order.product_name ?? '-' }}</span>
                            <span class="mx-1">·</span>
                            <span>Qty: {{ order.quantity }}</span>
                            <template v-if="order.type === 'flexo' && order.dimension_width && order.dimension_height">
                                <span class="mx-1">·</span>
                                <span>{{ order.dimension_width }} × {{ order.dimension_height }} cm</span>
                            </template>
                            <span class="mx-1">·</span>
                            <span>{{ useFormatTime(order.created_at) }}</span>
                        </div>
                    </div>
                </li>
            </ul>
        </div>

        <p class="mt-2 text-xs text-gray-500">{{ selectedOrderIds.length }} order dipilih</p>

        <div class="flex justify-end gap-2 mt-4">
            <Button label="Batal" style="tertiary" @click="isAddOrderOpen = false" />
            <Button
                label="Tambah"
                :disabled="!selectedOrderIds.length"
                :loading="isLoadingAddOrder"
                @click="submitAddOrder"
            />
        </div>
    </Dialog>
</template>
